import React, { useEffect } from 'react';
import io from 'socket.io-client';
import { ToastContainer } from 'react-toastify';
import { useEyeconxStore } from '../../store/eyeconxStore';
import {
    parseSmsRealtimeEvent,
    useSmsRealtimeStore,
} from '../../store/smsRealtimeStore';
import { parseIncomingNotification } from '../smsgateway/pages/notifications/notificationUtils';
import notificationSound from './notification1.mp3';

const shouldEnableSocket = () => {
    const forceEnable = process.env.REACT_APP_ENABLE_NOTIFICATIONS === 'true';
    if (forceEnable) {
        return true;
    }
    const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
    return !isLocalhost;
};

/**
 * Clinic Socket.IO client: badge/notifications + realtime SMS chat events.
 * Existing badge / localStorage behavior is preserved for SMS_RECEIVED.
 * SMS_STATUS updates chat delivery ticks only (no badge/sound).
 */
const ServerNotifications = ({ clinicId }) => {
    const serverUrl = process.env.REACT_APP_NOTIFICATION_SERVER_URL;
    const incrementNotifications = useEyeconxStore((state) => state.incrementNotifications);
    const incrementSmsInbox = useEyeconxStore((state) => state.incrementSmsInbox);
    const publishEvent = useSmsRealtimeStore((state) => state.publishEvent);
    const setConnected = useSmsRealtimeStore((state) => state.setConnected);

    useEffect(() => {
        if (!clinicId) {
            return undefined;
        }
        if (!shouldEnableSocket()) {
            console.log('Notifications are disabled in localhost environment. Set REACT_APP_ENABLE_NOTIFICATIONS=true to enable.');
            return undefined;
        }
        if (!serverUrl) {
            console.warn('REACT_APP_NOTIFICATION_SERVER_URL is not set; realtime notifications disabled.');
            return undefined;
        }

        const socket = io(serverUrl, {
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
        });
        const audio = new Audio(notificationSound);

        socket.on('connect', () => {
            console.log('Connected to notification server with Clinic ' + clinicId);
            socket.emit('registerClinic', String(clinicId));
            setConnected(true);
        });

        socket.on('disconnect', () => {
            setConnected(false);
        });

        socket.on('notification', (data) => {
            const realtimeEvent = parseSmsRealtimeEvent(data);
            if (realtimeEvent) {
                publishEvent(realtimeEvent);
            }

            const type = realtimeEvent?.notificationType || '';
            // Status updates must not bump badges or play sound
            if (type === 'SMS_STATUS') {
                return;
            }

            const newNotification = parseIncomingNotification(data);
            incrementNotifications();
            incrementSmsInbox();
            const savedNotifications = localStorage.getItem('notifications');
            const prevNotifications = savedNotifications ? JSON.parse(savedNotifications) : [];
            const updatedNotifications = [...prevNotifications, newNotification];
            localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
            try {
                audio.play();
            } catch (e) {
                // Autoplay may be blocked; ignore
            }
        });

        return () => {
            socket.off('notification');
            socket.off('connect');
            socket.off('disconnect');
            socket.emit('leaveClinicRoom', clinicId);
            setConnected(false);
            socket.close();
        };
    }, [
        clinicId,
        serverUrl,
        incrementNotifications,
        incrementSmsInbox,
        publishEvent,
        setConnected,
    ]);

    return (
        <div>
            <ToastContainer position="top-right" />
        </div>
    );
};

export default ServerNotifications;
